import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGradientBackground } from '@/utils/gradientBackground';

interface ComparisonFeature {
  feature: string;
  starter: string | React.ReactNode;
  basic: string | React.ReactNode;
  pro: string | React.ReactNode;
}

const comparisonFeatures: ComparisonFeature[] = [
  {
    feature: 'Type of engagement',
    starter: 'Brand-only foundation',
    basic: 'Brand + website',
    pro: 'Strategy, brand, site + light systems',
  },
  {
    feature: 'Brand strategy call',
    starter: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>30–60 min call</span>
      </span>
    ),
    basic: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>30–60 min call</span>
      </span>
    ),
    pro: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>60–90 min deep dive</span>
      </span>
    ),
  },
  {
    feature: 'Visual identity (logo, colors, type)',
    starter: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Included</span>
      </span>
    ),
    basic: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Included</span>
      </span>
    ),
    pro: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Included</span>
      </span>
    ),
  },
  {
    feature: 'Brand guide PDF',
    starter: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>1–2 page starter guide</span>
      </span>
    ),
    basic: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Extended version for site + proposals</span>
      </span>
    ),
    pro: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Extended guide + messaging highlights</span>
      </span>
    ),
  },
  {
    feature: 'Website pages',
    starter: <span className="text-slate-400">—</span>,
    basic: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>4–6 pages</span>
      </span>
    ),
    pro: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>4–6 core pages + optional extras (process/case studies/resources)</span>
      </span>
    ),
  },
  {
    feature: 'Conversion-focused copywriting',
    starter: <span className="text-slate-400">— (only light guidance)</span>,
    basic: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Included for core pages</span>
      </span>
    ),
    pro: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Included, with added emphasis on positioning and offers</span>
      </span>
    ),
  },
  {
    feature: 'Tech setup (domain, hosting, SSL)',
    starter: <span className="text-slate-400">— (not included)</span>,
    basic: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Included</span>
      </span>
    ),
    pro: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Included</span>
      </span>
    ),
  },
  {
    feature: 'Forms & booking',
    starter: <span className="text-slate-400">— (not included)</span>,
    basic: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Contact and/or "Book a call" form set up and tested</span>
      </span>
    ),
    pro: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Forms + notifications + basic lead capture flow</span>
      </span>
    ),
  },
  {
    feature: 'CRM / light systems',
    starter: <span className="text-slate-400">— (not included)</span>,
    basic: <span className="text-slate-400">Basic integrations only (if needed)</span>,
    pro: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Simple CRM and pipeline setup for new leads</span>
      </span>
    ),
  },
  {
    feature: 'Launch plan',
    starter: <span className="text-slate-400">Basic next steps guidance</span>,
    basic: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Simple launch checklist</span>
      </span>
    ),
    pro: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>Detailed 30–60 day launch plan</span>
      </span>
    ),
  },
  {
    feature: 'Follow-up strategy calls',
    starter: <span className="text-slate-400">— None</span>,
    basic: <span className="text-slate-400">Optional add-on</span>,
    pro: (
      <span className="flex items-center gap-2">
        <Check className="w-4 h-4 text-blue-600" />
        <span>2–3 follow-up calls in first 60–90 days</span>
      </span>
    ),
  },
  {
    feature: 'Starting investment',
    starter: 'From $2,000',
    basic: 'From $5,000',
    pro: 'From $10,000',
  },
];

export default function ServicesComparisonSection() {
  const sectionReveal = useRevealOnce({ amount: 0.2 });
  const backgroundStyle = useGradientBackground();

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' as const },
    },
  };

  return (
    <>
          {/* Main Section */}
          <section className="relative w-full py-16 md:py-24" style={backgroundStyle}>
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <motion.div
              ref={sectionReveal.ref}
              variants={containerVariants}
              initial="hidden"
              animate={sectionReveal.controls}
            >
              {/* Heading Section */}
              <div className="text-center mb-12 md:mb-16">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
                  Compare packages at a glance
                </h2>
                <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
                  Here's what you get with each level. Use this as a quick reference alongside the package cards above.
                </p>
              </div>

              {/* Comparison Table */}
              <div className="mb-12 md:mb-16 overflow-x-auto">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-semibold text-slate-900 min-w-[200px]">
                        Feature
                      </TableHead>
                      <TableHead className="font-semibold text-slate-900 text-left min-w-[150px]">
                        Starter
                      </TableHead>
                      <TableHead className="font-semibold text-slate-900 text-left min-w-[150px]">
                        Basic
                      </TableHead>
                      <TableHead className="font-semibold text-slate-900 text-left min-w-[150px]">
                        Pro
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comparisonFeatures.map((row, index) => (
                      <TableRow
                        key={index}
                        className="border-b border-slate-200 hover:bg-slate-50/50"
                      >
                        <TableCell className="font-medium text-slate-700 py-4">
                          {row.feature}
                        </TableCell>
                        <TableCell className="text-slate-900 text-left py-4">{row.starter}</TableCell>
                        <TableCell className="text-slate-900 text-left py-4">{row.basic}</TableCell>
                        <TableCell className="text-slate-900 text-left py-4">{row.pro}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Small CTA Link */}
              <div className="text-center">
                <p className="text-slate-600 mb-2">
                  Not sure which fit is right?{' '}
                  <Link
                    to="/contact"
                    className="text-blue-600 hover:text-blue-700 hover:underline font-medium transition-colors duration-200"
                  >
                    Book a free 1-hour consult →
                  </Link>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="w-full h-8 bg-gradient-to-b from-white via-neutral-900 to-black" />
    </>
  );
}

